import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api, absUrl } from "../../lib/api";
import { setFavicon } from "../../lib/favicon";
import { PageHeader, AdminBody, Card, Button } from "../../components/admin/Bits";
import MediaPicker from "../../components/admin/MediaPicker";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(r => setS(r.data || {})); }, []);

  const update = (path, value) => {
    setS(prev => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = { ...(cur[parts[i]] || {}) }; cur = cur[parts[i]]; }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      if (s.logo_url) setFavicon(absUrl(s.logo_url));
      toast.success("Site settings saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!s) return <div className="p-10">Loading…</div>;

  return (
    <>
      <PageHeader title="Site Settings" subtitle="Edit everything visible on the public website — without touching code."
        action={<Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save Changes"}</Button>} />
      <AdminBody>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="label-eyebrow mb-4">Brand</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SettingsField s={s} update={update} label="Business Name" path="business_name" />
              <SettingsField s={s} update={update} label="Tagline" path="tagline" />
              <SettingsField s={s} update={update} label="Founder Name" path="founder_name" />
              <SettingsField s={s} update={update} label="Founder Title" path="founder_title" />
              <div className="sm:col-span-2">
                <label className="label">Logo</label>
                <MediaPicker value={s.logo_url} onChange={v => update("logo_url", v)} data-testid="logo-picker" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">Contact</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SettingsField s={s} update={update} label="Phone" path="phone" />
              <SettingsField s={s} update={update} label="WhatsApp" path="whatsapp" />
              <SettingsField s={s} update={update} label="Email" path="email" />
              <SettingsField s={s} update={update} label="GST Number" path="gst_number" />
              <div className="sm:col-span-2"><SettingsField s={s} update={update} label="Address" path="address" textarea /></div>
              <div className="sm:col-span-2"><SettingsField s={s} update={update} label="Google Maps Embed URL" path="google_maps_url" /></div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">Hero</div>
            <SettingsField s={s} update={update} label="Headline" path="hero.headline" />
            <div className="mt-3"><SettingsField s={s} update={update} label="Sub" path="hero.sub" textarea /></div>
            <div className="mt-3">
              <label className="label">Hero Image</label>
              <MediaPicker value={s.hero?.image_url} onChange={v => update("hero.image_url", v)} />
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">Stats Strip</div>
            <div className="grid grid-cols-2 gap-3">
              <SettingsField s={s} update={update} label="Events" path="stats.events" />
              <SettingsField s={s} update={update} label="Cuisines" path="stats.cuisines" />
              <SettingsField s={s} update={update} label="Years" path="stats.years" />
              <SettingsField s={s} update={update} label="Cities" path="stats.cities" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">Social Media</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SettingsField s={s} update={update} label="Instagram URL" path="socials.instagram" />
              <SettingsField s={s} update={update} label="Facebook URL" path="socials.facebook" />
              <SettingsField s={s} update={update} label="YouTube URL" path="socials.youtube" />
              <SettingsField s={s} update={update} label="Twitter URL" path="socials.twitter" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="label-eyebrow mb-4">SEO</div>
            <SettingsField s={s} update={update} label="SEO Title" path="seo.title" />
            <div className="mt-3"><SettingsField s={s} update={update} label="Description" path="seo.description" textarea /></div>
            <div className="mt-3"><SettingsField s={s} update={update} label="Keywords" path="seo.keywords" /></div>
          </Card>

          <Card className="p-6 lg:col-span-2">
            <div className="label-eyebrow mb-4">Footer</div>
            <SettingsField s={s} update={update} label="Copyright Line" path="footer.copy" />
          </Card>
        </div>
      </AdminBody>
    </>
  );
}

// Defined OUTSIDE Settings so it keeps a stable identity across re-renders.
// (Previously this was declared inside Settings() as `const F = (...) => ...`,
// which created a brand-new component type on every keystroke, causing React
// to unmount/remount the <input> each time and lose focus after one character.)
function SettingsField({ s, update, label, path, type = "text", placeholder, textarea }) {
  return (
    <div>
      <label className="label">{label}</label>
      {textarea
        ? <textarea className="input min-h-[80px]" value={pathGet(s, path) || ""} onChange={e => update(path, e.target.value)} placeholder={placeholder} data-testid={`set-${path}`} />
        : <input className="input" type={type} value={pathGet(s, path) || ""} onChange={e => update(path, e.target.value)} placeholder={placeholder} data-testid={`set-${path}`} />}
    </div>
  );
}

function pathGet(o, path) {
  if (!o) return "";
  return path.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
}